import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      toast.success(mode === "login" ? "Welcome back" : "Welcome to rendezvous");
      navigate(mode === "login" ? "/app" : "/onboarding");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="auth-page">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-[#1A1818]">
        <img
          src="https://images.unsplash.com/photo-1769103638504-a42003f79dd0?w=1200&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1818]/40 via-transparent to-[#1A1818]/60" />
        <div className="relative z-10 p-12 self-end text-white">
          <div className="text-xs uppercase tracking-[0.3em] opacity-80">a private vol.</div>
          <h2 className="font-serif text-5xl italic mt-3 leading-tight">
            Write the first<br />paragraph.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="p-6 sm:p-10 flex items-center justify-between">
          <Link to="/" className="font-serif italic text-2xl text-[#913831]" data-testid="auth-brand-logo">
            rendezvous
          </Link>
          <button
            data-testid="toggle-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-[#737373] hover:text-[#913831] transition-colors"
          >
            {mode === "login" ? "Need an account? Join" : "Have an account? Sign in"}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <h1 className="font-serif text-5xl tracking-tight text-[#1A1818]">
              {mode === "login" ? "Sign back in." : "Begin a quiet sign‑up."}
            </h1>
            <p className="mt-3 text-[#737373]">
              {mode === "login"
                ? "Pick up where you left off."
                : "Just a name, an address, a passphrase. Profile comes next."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
              {mode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Name</label>
                  <input
                    data-testid="input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Amelia"
                    className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-lg font-serif"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Email</label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@somewhere.com"
                  className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-lg"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Passphrase</label>
                <input
                  data-testid="input-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="At least six characters"
                  className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-lg"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-auth"
                disabled={busy}
                className="w-full mt-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-60 text-white rounded-full py-4 font-medium transition-transform hover:scale-[1.01] active:scale-95 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={18} className="animate-spin" />}
                {mode === "login" ? "Sign in" : "Create my profile"}
              </button>

              {mode === "login" && (
                <div className="text-xs text-[#737373] text-center pt-2">
                  Try demo: <span className="font-mono">amelia@demo.com</span> / <span className="font-mono">demo1234</span>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
